// src/encounter/lib/patient-context.ts
//
// PROD-3042 · the template's own sex and age conditions, with the patient as launch context (Samer's ruling,
// 6 Oct 2026: "the template must be consistent with the clinical case, through the conditional display our
// knowledge base already chose" — CONTRACT-template-to-encounter-v1 "Patient: age band, sex → sex-specific
// items"; vendor matrix capabilities 9 and 11, FormBox). Nothing is hidden by product code: an item, group or
// option is left out ONLY when the TEMPLATE says so for this patient. No term lists, no reading of words.
//
// WHAT THE TEMPLATE CAN SAY (standard SDC, read from the FHIR template before it is mapped to the DS shape):
//   · an item or group: `sdc-questionnaire-enableWhenExpression` — drawn only when true;
//   · individual options: `sdc-questionnaire-answerOptionsToggleExpression` — the listed options are offered
//     only when its expression is true; options it does not list are always offered.
//
// HOW IT IS EVALUATED — NO NEW EVALUATOR. The expressions are read only in their launch-context forms:
//     %patient.gender = 'female'                  (or !=, any AdministrativeGender code)
//     %patient.birthDate <= today() - 18 years    (or <, >=, >; an age band in whole years)
// joined by all-`and` or all-`or`. Each clause becomes a FHIR enableWhen condition, and the DS renderer's own
// enableWhen semantics decide it (`qrItemEnabled`: its operators, its `enableBehavior` all/any). A date
// compares as its yyyymmdd number, which orders as the dates do.
//
// WHEN NOTHING IS HIDDEN: no condition; an expression in any other form (unreadable here → offered, never
// guessed); or a condition about a fact the record does not hold — no sex on record hides nothing, and no
// birth date hides nothing.
//
// EXCLUDED FROM THE RESPONSE: an item the template disables for this patient is not drawn, and its answer is
// not sent (`withoutPatientExcluded`), as FHIR asks of a disabled item.
//
// WHAT QUESTIONBANK STORES TODAY (6 Oct): it drops both SDC extensions above on write, and keeps answer-based
// `enableWhen` / `enableBehavior` and `calculatedExpression`. So the template carries HIDDEN CONTEXT ITEMS —
// a read-only item whose calculatedExpression is a launch-context path (`ctx-patient-sex` =
// `%patient.gender`) — and sex- or age-specific items use plain answer-based enableWhen on them
// (`scopeToPatient`):
//   (a) a context item is a `ctx-` item whose calculatedExpression is a launch-context path; its value comes
//       from the patient (`%patient.gender` → the AdministrativeGender code, `%patient.birthDate` → the ISO
//       date); no value on record → no value;
//   (b) the context item itself is never drawn and never answered;
//   (c) an enableWhen condition on a context item is decided here, by the DS `qrEnableWhenPass`, and then
//       taken out of the item's enableWhen (the renderer would otherwise test it against an answer nobody
//       gives); a failed item leaves the questionnaire and the response. A condition on a value not on record
//       passes: no sex on record hides nothing.
// The extension forms stay supported for the day QuestionBank stores them.
import { qrEnableWhenPass, qrItemEnabled, type QEnableWhen, type QItem, type Questionnaire, type QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
import type { FhirExtension, FhirQuestionnaire, FhirQuestionnaireItem } from '@/types/template-studio-api';

export const SDC_ENABLE_WHEN_EXPRESSION_URL = 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-enableWhenExpression';
export const SDC_ANSWER_OPTIONS_TOGGLE_URL = 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-answerOptionsToggleExpression';

/** The launch-context patient: only the facts a template condition may read. */
export interface LaunchPatient {
  /** FHIR AdministrativeGender code (`male` · `female` · `other` · `unknown`). Absent: no sex on record. */
  gender?: string;
  /** ISO date (YYYY-MM-DD…). Absent: no birth date on record. */
  birthDate?: string;
}

/** The page's registration sex ('M' / 'F') and ISO birth date → the launch patient. Anything else: absent. */
export function toLaunchPatient(sex: string | null | undefined, birthDateIso: string | null | undefined): LaunchPatient {
  const gender = sex === 'M' ? 'male' : sex === 'F' ? 'female' : undefined;
  const birthDate = typeof birthDateIso === 'string' && /^\d{4}-\d{2}-\d{2}/.test(birthDateIso) ? birthDateIso.slice(0, 10) : undefined;
  return { ...(gender ? { gender } : {}), ...(birthDate ? { birthDate } : {}) };
}

/** A date as the number its yyyymmdd digits make (ordering is preserved). */
const dayNumber = (iso: string): number => Number(iso.slice(0, 10).replace(/-/g, ''));

/** Today minus `years`, as yyyymmdd. */
function yearsBefore(today: Date, years: number): number {
  return (today.getFullYear() - years) * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
}

const GENDER_CLAUSE = /^%patient\.gender\s*(=|!=)\s*'([a-z-]+)'$/;
const BIRTH_CLAUSE = /^%patient\.birthDate\s*(<=|<|>=|>)\s*today\(\)\s*-\s*(\d{1,3})\s+'?years?'?$/;

/** A launch-context expression as enableWhen conditions on the two context facts, or undefined when it is
 *  not one of the forms above (it is then never evaluated, and hides nothing). */
export function contextConditions(expression: string, today: Date): { conditions: QEnableWhen[]; behavior: 'all' | 'any' } | undefined {
  const text = expression.trim();
  if (text === '') return undefined;
  const hasAnd = /\s+and\s+/.test(text);
  const hasOr = /\s+or\s+/.test(text);
  if (hasAnd && hasOr) return undefined;
  const clauses = text.split(hasOr ? /\s+or\s+/ : /\s+and\s+/).map((c) => c.trim());
  const conditions: QEnableWhen[] = [];
  for (const clause of clauses) {
    const g = GENDER_CLAUSE.exec(clause);
    if (g) {
      conditions.push({ question: 'gender', operator: g[1] as '=' | '!=', answer: g[2] });
      continue;
    }
    const b = BIRTH_CLAUSE.exec(clause);
    if (b) {
      conditions.push({ question: 'birthDate', operator: b[1] as '<=' | '<' | '>=' | '>', answer: yearsBefore(today, Number(b[2])) });
      continue;
    }
    return undefined;
  }
  return { conditions, behavior: hasOr ? 'any' : 'all' };
}

/** Does the template's condition allow this patient? Unreadable, or about a fact not on record → yes. */
export function patientAllows(expression: string | null | undefined, patient: LaunchPatient, today: Date = new Date()): boolean {
  if (!expression) return true;
  const parsed = contextConditions(expression, today);
  if (!parsed) return true;
  const values: Record<string, string | number> = {};
  if (patient.gender) values.gender = patient.gender;
  if (patient.birthDate) values.birthDate = dayNumber(patient.birthDate);
  // A fact the record does not hold hides nothing (no sex on record = nothing hidden).
  if (parsed.conditions.some((c) => values[c.question] === undefined)) return true;
  // The DS renderer's enableWhen semantics decide (operators + enableBehavior), not a second evaluator.
  return qrItemEnabled({ linkId: 'launch-context', type: 'group', enableWhen: parsed.conditions, enableBehavior: parsed.behavior } as QItem, values);
}

/** What the template leaves out for this patient: whole items (by linkId) and options (by option key). */
export interface PatientExclusions {
  items: ReadonlySet<string>;
  /** linkId → option keys (the DS key: `code ?? display`, or the valueString). */
  options: ReadonlyMap<string, ReadonlySet<string>>;
}

export const NO_EXCLUSIONS: PatientExclusions = { items: new Set(), options: new Map() };

const expressionOf = (e: FhirExtension | undefined): string | undefined => e?.valueExpression?.expression ?? undefined;

/** The toggle extension's parts: the listed options and the expression (SDC: `option` ×n + `expression`). */
interface ToggleParts {
  options: string[];
  expression?: string;
}
function toggleParts(ext: FhirExtension): ToggleParts {
  const parts = ((ext as FhirExtension & { extension?: (FhirExtension & { valueString?: string | null })[] | null }).extension ?? []) as (FhirExtension & {
    valueString?: string | null;
  })[];
  const options: string[] = [];
  let expression: string | undefined;
  for (const p of parts) {
    if (p.url === 'option') {
      const key = p.valueCoding?.code ?? p.valueCoding?.display ?? p.valueString ?? undefined;
      if (key) options.push(key);
    } else if (p.url === 'expression') {
      expression = expressionOf(p);
    }
  }
  return { options, expression };
}

/** Walk the FHIR template: every item/group whose enableWhenExpression fails for this patient (with all it
 *  holds), and every option an answerOptionsToggleExpression switches off. */
export function patientExclusionsOf(questionnaire: FhirQuestionnaire | null | undefined, patient: LaunchPatient, today: Date = new Date()): PatientExclusions {
  if (!questionnaire) return NO_EXCLUSIONS;
  const items = new Set<string>();
  const options = new Map<string, Set<string>>();
  const addAll = (it: FhirQuestionnaireItem) => {
    items.add(it.linkId);
    (it.item ?? []).forEach(addAll);
  };
  const walk = (list: readonly FhirQuestionnaireItem[] | null | undefined) => {
    for (const it of list ?? []) {
      const ewe = expressionOf((it.extension ?? []).find((e) => e.url === SDC_ENABLE_WHEN_EXPRESSION_URL));
      if (!patientAllows(ewe, patient, today)) {
        addAll(it);
        continue;
      }
      // SDC: an option listed by several toggle expressions is offered when ANY of them is true (Codex review).
      const listedBy = new Map<string, boolean>();
      for (const ext of (it.extension ?? []).filter((e) => e.url === SDC_ANSWER_OPTIONS_TOGGLE_URL)) {
        const { options: listed, expression } = toggleParts(ext);
        const on = patientAllows(expression, patient, today);
        for (const k of listed) listedBy.set(k, (listedBy.get(k) ?? false) || on);
      }
      const off = [...listedBy].filter(([, on]) => !on).map(([k]) => k);
      if (off.length > 0) options.set(it.linkId, new Set(off));
      walk(it.item);
    }
  };
  walk(questionnaire.item);
  return items.size === 0 && options.size === 0 ? NO_EXCLUSIONS : { items, options };
}

const optionKey = (o: NonNullable<QItem['answerOption']>[number]): string | undefined =>
  o.valueCoding?.code ?? o.valueCoding?.display ?? (o as { valueString?: string }).valueString;

/** The DS questionnaire without what the template leaves out for this patient (same reference when nothing). */
export function withoutPatientExclusions(questionnaire: Questionnaire, ex: PatientExclusions): Questionnaire {
  if (ex.items.size === 0 && ex.options.size === 0) return questionnaire;
  const prune = (list: readonly QItem[]): QItem[] =>
    list
      .filter((it) => !ex.items.has(it.linkId))
      .map((it) => {
        const off = ex.options.get(it.linkId);
        const answerOption = off && it.answerOption ? it.answerOption.filter((o) => !off.has(optionKey(o) ?? '')) : it.answerOption;
        const next = answerOption !== it.answerOption ? { ...it, answerOption } : it;
        return next.item ? { ...next, item: prune(next.item) } : next;
      });
  return { ...questionnaire, item: prune(questionnaire.item) };
}

/** The response without the answers of items the template disables for this patient (FHIR: a disabled item
 *  carries no answer), and without option values it switches off (a repeating answer keeps its other values).
 *  Same reference when nothing is removed. */
export function withoutPatientExcluded(response: QuestionnaireResponse, ex: PatientExclusions): QuestionnaireResponse {
  if (ex.items.size === 0 && ex.options.size === 0) return response;
  let changed = false;
  const answers: QuestionnaireResponse['answers'] = [];
  for (const a of response.answers) {
    if (ex.items.has(a.linkId)) {
      changed = true;
      continue;
    }
    const off = ex.options.get(a.linkId);
    if (off && Array.isArray(a.value)) {
      const kept = (a.value as unknown[]).filter((v) => !off.has(String(v)));
      if (kept.length !== a.value.length) {
        changed = true;
        if (kept.length > 0) answers.push({ ...a, value: kept as typeof a.value });
        continue;
      }
    } else if (off && a.value !== undefined && a.value !== null && off.has(String(a.value))) {
      changed = true;
      continue;
    }
    answers.push(a);
  }
  return changed ? { ...response, answers } : response;
}

// ── Hidden context items (what QuestionBank stores today) ───────────────────────────────────────

/** A calculatedExpression that IS a launch-context path → which fact it reads. Anything else is not one. */
const CONTEXT_PATH = /^%patient\.(gender|birthDate)$/;

/** The context item's value for this patient, or undefined when the record does not hold it. */
function contextValue(expression: string, patient: LaunchPatient): string | undefined {
  const m = CONTEXT_PATH.exec(expression.trim());
  if (!m) return undefined;
  return m[1] === 'gender' ? patient.gender : patient.birthDate;
}

/** The designation of a hidden context item that survives QuestionBank (its `questionnaire-hidden` extension does
 *  not): the `ctx-` linkId prefix the template lane uses (`ctx-patient-sex`, `ctx-age-band`). A visible item that
 *  merely calculates from the patient is NOT a context item and is drawn as authored (Codex review). */
export const CONTEXT_ITEM_PREFIX = 'ctx-';

/** Context items in the tree: `ctx-` leaf items whose calculatedExpression is a launch-context path. */
export function contextItemValues(questionnaire: Questionnaire, patient: LaunchPatient): Map<string, string | undefined> {
  const out = new Map<string, string | undefined>();
  const walk = (list: readonly QItem[]) => {
    for (const it of list) {
      if (
        it.type !== 'group' &&
        it.linkId.startsWith(CONTEXT_ITEM_PREFIX) &&
        typeof it.calculatedExpression === 'string' &&
        CONTEXT_PATH.test(it.calculatedExpression.trim())
      ) {
        out.set(it.linkId, contextValue(it.calculatedExpression, patient));
      }
      walk(it.item ?? []);
    }
  };
  walk(questionnaire.item);
  return out;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
/** One condition on a context item, by the DS enableWhen semantics. A date on both sides compares as yyyymmdd. */
function contextConditionPasses(cond: QEnableWhen, value: string): boolean {
  if (typeof cond.answer === 'string' && ISO_DATE.test(cond.answer) && ISO_DATE.test(value)) {
    return qrEnableWhenPass({ ...cond, answer: dayNumber(cond.answer) }, dayNumber(value));
  }
  return qrEnableWhenPass(cond, value);
}

/**
 * The DS questionnaire scoped to this patient through its hidden context items: context items removed, every
 * enableWhen condition on them decided and taken out, failed items (with all they hold) removed. Returns the
 * linkIds removed, so their answers leave the response too. Same reference when the template has no context item.
 */
export function resolveContextItems(questionnaire: Questionnaire, patient: LaunchPatient): { questionnaire: Questionnaire; removed: ReadonlySet<string> } {
  const context = contextItemValues(questionnaire, patient);
  if (context.size === 0) return { questionnaire, removed: new Set() };
  const removed = new Set<string>();
  const removeAll = (it: QItem) => {
    removed.add(it.linkId);
    (it.item ?? []).forEach(removeAll);
  };
  const prune = (list: readonly QItem[]): QItem[] => {
    const out: QItem[] = [];
    for (const it of list) {
      if (context.has(it.linkId)) {
        removeAll(it);
        continue;
      }
      const ew = it.enableWhen ?? [];
      const onContext = ew.filter((c) => context.has(c.question));
      let next: QItem = it;
      if (onContext.length > 0) {
        // A condition on a value not on record passes (nothing hidden for a fact the record lacks).
        const results = onContext.map((c) => {
          const v = context.get(c.question);
          return v === undefined ? true : contextConditionPasses(c, v);
        });
        const rest = ew.filter((c) => !context.has(c.question));
        const any = it.enableBehavior === 'any';
        const enabled = any ? results.some(Boolean) : results.every(Boolean);
        if (!any && !enabled) {
          removeAll(it);
          continue;
        }
        if (any && !enabled && rest.length === 0) {
          removeAll(it);
          continue;
        }
        // all: the context part passed, the answer-based rest still decides. any: a passing context condition
        // enables the item outright; a failing one leaves the rest to decide.
        const kept = any && enabled ? [] : rest;
        next = { ...it, enableWhen: kept.length > 0 ? kept : undefined };
        if (kept.length === 0) delete (next as { enableBehavior?: unknown }).enableBehavior;
      }
      out.push(next.item ? { ...next, item: prune(next.item) } : next);
    }
    return out;
  };
  return { questionnaire: { ...questionnaire, item: prune(questionnaire.item) }, removed };
}

/**
 * Everything the template says about THIS patient, in one place for the page: the SDC extensions (read from the
 * FHIR template) and the hidden context items (read from the DS questionnaire). `exclusions.items` is what the
 * response must not carry.
 */
export function scopeToPatient(
  questionnaire: Questionnaire,
  fhir: FhirQuestionnaire | null | undefined,
  patient: LaunchPatient,
  today: Date = new Date(),
): { questionnaire: Questionnaire; exclusions: PatientExclusions } {
  const fromExtensions = patientExclusionsOf(fhir, patient, today);
  const { questionnaire: scoped, removed } = resolveContextItems(withoutPatientExclusions(questionnaire, fromExtensions), patient);
  if (removed.size === 0) return { questionnaire: scoped, exclusions: fromExtensions };
  return { questionnaire: scoped, exclusions: { items: new Set([...fromExtensions.items, ...removed]), options: fromExtensions.options } };
}
